import { z } from 'zod';

const Hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Farbe als #RRGGBB angeben').transform((v) => v.toUpperCase());

/** Schriften wie in der PHP-Middleware (BRANDING_SCHRIFTEN) – selbst ausgeliefert, nicht über Google Fonts */
export const SCHRIFTEN = ['Montserrat', 'DM Sans', 'Poppins', 'Nunito Sans', 'Source Sans 3', 'Lato', 'Open Sans', 'Raleway'] as const;
export type Schrift = (typeof SCHRIFTEN)[number];

/** CSS-Familie je Schrift (Namen der Fontsource-Pakete) */
export const SCHRIFT_FAMILIE: Record<Schrift, string> = {
  Montserrat: 'Montserrat Variable',
  'DM Sans': 'DM Sans Variable',
  Poppins: 'Poppins',
  'Nunito Sans': 'Nunito Sans Variable',
  'Source Sans 3': 'Source Sans 3 Variable',
  Lato: 'Lato',
  'Open Sans': 'Open Sans Variable',
  Raleway: 'Raleway Variable',
};

/** Bilder je Marktplatz – längste Kante in Pixeln wie BRANDING_BILDER der PHP-Middleware */
export const BILD_ARTEN = {
  logoHell: { titel: 'Logo für helle Flächen', kante: 1600 },
  logoDunkel: { titel: 'Logo für dunkle Flächen', kante: 1600 },
  icon: { titel: 'App-Icon', kante: 512 },
  titelbild: { titel: 'Titelbild der Anmeldung', kante: 2400 },
} as const;
export type BildArt = keyof typeof BILD_ARTEN;
export const BildArtSchema = z.enum(Object.keys(BILD_ARTEN) as [BildArt, ...BildArt[]]);
/** Gespeichert werden höchstens 1 MB – größere Bilder verkleinert die Oberfläche vorher */
export const BILD_MAX_BYTES = 1_000_000;
export const BILD_TYPEN = ['image/png', 'image/jpeg', 'image/webp'] as const;

const Farben = z.object({
  haupt: Hex,
  akzent: Hex,
  button: Hex,
  buttonText: Hex,
  hintergrund: Hex,
  flaeche: Hex,
  navigation: Hex,
  navigationText: Hex,
  text: Hex,
  textGedaempft: Hex,
  linie: Hex,
});
export type Farben = z.infer<typeof Farben>;

/* ---------- E-Mail-Design ---------- */

/** Websichere Schriften für E-Mails – keine Webfonts, die Programme laden sie ohnehin nicht zuverlässig */
export const MAIL_SCHRIFTEN = {
  system: { titel: 'Systemschrift', css: "-apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" },
  arial: { titel: 'Arial', css: 'Arial, Helvetica, sans-serif' },
  helvetica: { titel: 'Helvetica', css: "Helvetica, Arial, sans-serif" },
  verdana: { titel: 'Verdana', css: 'Verdana, Geneva, sans-serif' },
  tahoma: { titel: 'Tahoma', css: 'Tahoma, Verdana, sans-serif' },
  trebuchet: { titel: 'Trebuchet MS', css: "'Trebuchet MS', Helvetica, Arial, sans-serif" },
  georgia: { titel: 'Georgia', css: "Georgia, 'Times New Roman', Times, serif" },
  times: { titel: 'Times New Roman', css: "'Times New Roman', Times, serif" },
} as const;
export type MailSchrift = keyof typeof MAIL_SCHRIFTEN;

export const MAIL_KNOPF = { eckig: 'Eckig', abgerundet: 'Abgerundet', rund: 'Rund' } as const;
export type MailKnopf = keyof typeof MAIL_KNOPF;

const MailFarben = z.object({
  /** Rand der Karte, Buttons und Links */
  akzent: Hex,
  /** Schrift auf dem Button */
  knopfText: Hex,
  hintergrund: Hex,
  karte: Hex,
  text: Hex,
});
export type MailFarben = z.infer<typeof MailFarben>;

/** Hochgeladenes Mail-Bild (öffentliche Adresse, unveränderlich je Upload) */
const MailBildUrl = z.string().max(300).regex(/^\/api\/oeffentlich\/mailbild\/[a-z0-9/-]+\.(png|jpg|webp|gif)$/, 'Bitte das Bild erneut hochladen').nullable().default(null);

/** Bilder im E-Mail-Design – Upload als Datei, höchstens 2 MB, kein SVG */
export const MAIL_BILD_ARTEN = {
  logo: { titel: 'Logo für E-Mails', feld: 'logoBild', kante: 2000 },
  banner: { titel: 'Banner', feld: 'bannerBild', kante: 3000 },
  fuss: { titel: 'Bild in der Fußzeile', feld: 'fussBild', kante: 2000 },
} as const;
export type MailBildArt = keyof typeof MAIL_BILD_ARTEN;
export const MAIL_BILD_TYPEN = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
export const MAIL_BILD_MAX_BYTES = 2 * 1024 * 1024;

export interface MailBildAntwort {
  url: string;
  breite: number;
  hoehe: number;
}

/** E-Mail-Design: Teil des Brandings. null = erben (Marktplatz → DXL → Standarddesign) */
export const MailDesignSchema = z.object({
  /** Kopfzeile: Logo aus dem Branding (ohne Logo: Name) oder nur der Name */
  kopf: z.enum(['logo', 'text']).default('logo'),
  /** Namen des Absenders zusätzlich neben dem Logo zeigen */
  nameImKopf: z.boolean().default(false),
  farben: MailFarben,
  schrift: z.enum(Object.keys(MAIL_SCHRIFTEN) as [MailSchrift, ...MailSchrift[]]).default('system'),
  knopf: z.enum(Object.keys(MAIL_KNOPF) as [MailKnopf, ...MailKnopf[]]).default('abgerundet'),
  /** Eigenes Logo für Mails (Upload), sonst das Logo aus dem Branding, sonst der Name */
  logoBild: MailBildUrl,
  /** Breite des Logos in Pixeln */
  logoBreite: z.number().int().min(80).max(240).default(140),
  logoAusrichtung: z.enum(['links', 'mitte']).default('links'),
  /** Banner unter der Kopfzeile, volle Kartenbreite */
  bannerBild: MailBildUrl,
  bannerLink: z.union([z.literal(''), z.string().trim().max(500).regex(/^https:\/\/[^\s"'<>]+$/, 'Bitte eine Adresse mit https:// angeben')]).default(''),
  bannerAlt: z.string().trim().max(200, 'Höchstens 200 Zeichen').default(''),
  /** Bild in der Fußzeile, z. B. Siegel oder Signatur */
  fussBild: MailBildUrl,
  fussBildBreite: z.number().int().min(40).max(240).default(120),
  /** Fußzeile, z. B. Firmenangaben und Impressum – mehrzeilig, nur Text */
  fusszeile: z
    .string()
    .max(1000, 'Höchstens 1.000 Zeichen')
    .transform((t) => t.replace(/\r\n?/g, '\n').split('\n').map((zeile) => zeile.trimEnd()).join('\n').trim().replace(/\n{3,}/g, '\n\n'))
    .default(''),
});
export type MailDesign = z.infer<typeof MailDesignSchema>;
export type MailDesignEingabe = z.input<typeof MailDesignSchema>;

/** Speichern und Test-Mail: eigenes Design oder null (erben) */
export const MailDesignSpeichernSchema = z.object({ design: MailDesignSchema.nullable() });

/** Was der Editor speichert. Bilder kommen über eigene Uploads dazu. */
export const BrandingEingabeSchema = z.object({
  erscheinungsbild: z.enum(['hell', 'dunkel']),
  farben: Farben,
  ecken: z.number().int().min(0).max(16),
  schrift: z.enum(SCHRIFTEN).default('Montserrat'),
  /** Deckkraft des Anmeldeformulars über dem Titelbild in Prozent */
  formularDeckkraft: z.union([z.literal(100), z.literal(85), z.literal(70)]).default(85),
  /** „Bereitgestellt von DXL“ – nur mit Whitelabel-Lizenz abschaltbar */
  dxlHinweis: z.boolean().default(true),
});
export type BrandingEingabe = z.infer<typeof BrandingEingabeSchema>;

/** Adresse eines hochgeladenen Bildes (versioniert) oder einer externen Grafik */
const BildUrl = z.string().regex(/^(\/api\/oeffentlich\/|https:\/\/)\S+$/).nullable().default(null);

export const BrandingSchema = BrandingEingabeSchema.extend({
  logoHell: BildUrl,
  logoDunkel: BildUrl,
  icon: BildUrl,
  titelbild: BildUrl,
});
export type Branding = z.infer<typeof BrandingSchema>;

/** DXL-Standard (Brand Guidelines: Ink + Teal, Montserrat) */
export const DXL_BRANDING: Branding = {
  erscheinungsbild: 'hell',
  farben: {
    haupt: '#101619',
    akzent: '#0F8F8A',
    button: '#0B7A76', // etwas dunkler als der Akzent: weiße Schrift erreicht WCAG AA (5,2:1)
    buttonText: '#FFFFFF',
    hintergrund: '#F2F5F5',
    flaeche: '#FFFFFF',
    // Seitenleiste liegt im DXL-Design auf der Hintergrundfarbe
    navigation: '#F2F5F5',
    navigationText: '#5B6670',
    text: '#101619',
    textGedaempft: '#5B6670',
    linie: '#DCE3E3',
  },
  ecken: 8,
  schrift: 'Montserrat',
  formularDeckkraft: 85,
  dxlHinweis: true,
  logoHell: null,
  logoDunkel: null,
  icon: null,
  titelbild: null,
};

/** Vorschlag beim Wechsel auf „Dunkel“ – Farben wie BRANDING_STANDARD der PHP-Middleware */
export const DXL_FARBEN_DUNKEL: Farben = {
  haupt: '#0F8F8A',
  akzent: '#16C3B3',
  button: '#16C3B3',
  buttonText: '#0B1013',
  hintergrund: '#101619',
  flaeche: '#1A2328',
  navigation: '#0B1013',
  navigationText: '#A7B5B6',
  text: '#F2F5F5',
  textGedaempft: '#A7B5B6',
  linie: '#2B373D',
};

/* ---------- Farbe & Kontrast (WCAG 2.1) ---------- */

function kanal(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

export function luminanz(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  return 0.2126 * kanal((n >> 16) & 255) + 0.7152 * kanal((n >> 8) & 255) + 0.0722 * kanal(n & 255);
}

export function kontrast(a: string, b: string): number {
  const [h, d] = [luminanz(a), luminanz(b)].sort((x, y) => y - x) as [number, number];
  return (h + 0.05) / (d + 0.05);
}

export interface KontrastBefund {
  paar: string;
  /** Farbe im Vordergrund – an diesem Feld zeigt der Editor den Hinweis */
  feld: keyof Farben;
  vorne: string;
  hinten: string;
  wert: number;
  mindestens: number;
  ok: boolean;
}

/** Alle lesbaren Farbpaare mit Kontrast – für die Anzeige im Editor */
export function kontrastPaare(b: Pick<Branding, 'farben'>): KontrastBefund[] {
  const f = b.farben;
  const paare: [string, keyof Farben, keyof Farben, number][] = [
    ['Text auf Hintergrund', 'text', 'hintergrund', 4.5],
    ['Text auf Fläche', 'text', 'flaeche', 4.5],
    ['Gedämpfter Text auf Fläche', 'textGedaempft', 'flaeche', 4.5],
    ['Button-Text auf Button', 'buttonText', 'button', 4.5],
    ['Navigation', 'navigationText', 'navigation', 4.5],
    ['Akzent auf Fläche', 'akzent', 'flaeche', 3],
  ];
  return paare.map(([paar, vorne, hinten, mindestens]) => {
    const wert = Math.round(kontrast(f[vorne], f[hinten]) * 100) / 100;
    return { paar, feld: vorne, vorne: f[vorne], hinten: f[hinten], wert, mindestens, ok: wert >= mindestens };
  });
}

/** Nur die Paare unter dem Mindestwert – der Server lehnt das Speichern dann ab */
export function brandingPruefen(b: Pick<Branding, 'farben'>): KontrastBefund[] {
  return kontrastPaare(b).filter((p) => !p.ok);
}

export function kontrastText(wert: number): string {
  return `${wert.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} : 1`;
}

/* ---------- Kontrast im E-Mail-Design ---------- */

export interface MailKontrastBefund extends Omit<KontrastBefund, 'feld'> {
  feld: keyof MailFarben;
}

export function mailKontrastPaare(d: Pick<MailDesign, 'farben'>): MailKontrastBefund[] {
  const f = d.farben;
  const paare: [string, keyof MailFarben, keyof MailFarben, number][] = [
    ['Text auf Karte', 'text', 'karte', 4.5],
    ['Text auf Hintergrund', 'text', 'hintergrund', 4.5],
    ['Button-Text auf Akzent', 'knopfText', 'akzent', 4.5],
    ['Akzent (Links) auf Karte', 'akzent', 'karte', 3],
  ];
  return paare.map(([paar, vorne, hinten, mindestens]) => {
    const wert = Math.round(kontrast(f[vorne], f[hinten]) * 100) / 100;
    return { paar, feld: vorne, vorne: f[vorne], hinten: f[hinten], wert, mindestens, ok: wert >= mindestens };
  });
}

export function mailDesignPruefen(d: Pick<MailDesign, 'farben'>): MailKontrastBefund[] {
  return mailKontrastPaare(d).filter((p) => !p.ok);
}
